import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { concatAndEncodeBase64, decodeJwtWithJsrs } from "../utils";
import {
  CLIENT_URL,
  XSM_BASE_URL,
  REACT_AUTH_ENDPOINT,
  SCOPE,
  USER_DOMAIN,
  XSM_APPLICATION,
  CLIENT_SECRET,
  COMMON_CALLBACK_URL
} from "../constants/auth";

// XSM-1536: SSO Login
export const useAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginCode = searchParams.get("code");
  const tokenRedirectUri = searchParams.get("tokenRedirectUri") || "";

  const [isLoading, setIsLoading] = useState(false);
  const hasProcessedLogin = useRef<string | null>(null);
  // localStorage tokens
  const ACCESS_TOKEN = localStorage.getItem("dm_access_token") ?? "";
  const ID_TOKEN = localStorage.getItem("dm_id_token") ?? "";
  const REFRESH_TOKEN = localStorage.getItem("dm_refresh_token") ?? "";

  // state for username and password
  const [state, setState] = useState<{ username: string; password: string }>({
    username: "",
    password: "",
  });

  const loginFormik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: () => { },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid Email"
        ),
      password: Yup.string().required("Password is required"),
    }),
  });

  // XSM-1536: SSO Login
  const initialAuthorization = async () => {
    try {
      const url = new URL(
        `${XSM_BASE_URL}/xsm-api-sso/v2/proxy/oauth2/authorize`
      );
      url.searchParams.set("response_type", "code");
      url.searchParams.set("client_id", CLIENT_URL as string);
      url.searchParams.set("redirect_uri", REACT_AUTH_ENDPOINT as string);
      url.searchParams.set("XSM-Domain", USER_DOMAIN as string);
      url.searchParams.set("Xsm-application", XSM_APPLICATION as string);
      url.searchParams.set("scope", "openid");

      window.location.assign(url.toString());
    } catch (error: any) {
      toast.error(error.toString() || "Initialization error");
    }
  };

  // XSM-1536: SSO Login
  const handleSubmitLogin = async (code: string) => {
    try {
      setIsLoading(true);
      if (code) {
        const redirectRequestUri = tokenRedirectUri
          ? window.atob(tokenRedirectUri)
          : REACT_AUTH_ENDPOINT;
        // Step 4: Call the API to exchange the code for an access token
        const tokenResponse = await fetch(
          `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Xsm-Domain": USER_DOMAIN,
              "xsm-application": XSM_APPLICATION,
            },
            body: new URLSearchParams({
              client_id: CLIENT_URL,
              grant_type: "authorization_code",
              redirect_uri: redirectRequestUri,
              code, // Use the accesscode obtained from Step 3
              scope: SCOPE,
            }),
          }
        );
        // Assuming the access token is available in the response
        const tokenData = await tokenResponse.json();
        //XSM Token
        const token = tokenData.access_token;
        const scope = tokenData.scope;
        const decodedToken = decodeJwtWithJsrs(token);
        //const userdomain = decodedToken.userdomain.toLowerCase();
        const userstatus = decodedToken?.userstatus
          ? decodedToken?.userstatus?.toLowerCase()
          : "";
        const userscope = decodedToken?.userscope
          ? decodedToken?.userscope
          : "";
        // Saving Expiry Token
        const expiresInMs = tokenData.expires_in * 1000; // convert to milliseconds
        const expirationTime = Date.now() + expiresInMs;
        localStorage.setItem("dm_expires_in", expirationTime.toString());
        localStorage.setItem("dm_access_token", token);
        localStorage.setItem("dm_scope", scope);
        localStorage.setItem("dm_id_token", tokenData?.id_token);
        localStorage.setItem("dm_user_name", decodedToken?.sub);
        if (userstatus) {
          localStorage.setItem("dm_userstatus", userstatus);
        }
        if (userscope) {
          localStorage.setItem("dm_userscope", JSON.stringify(userscope));
        }
        localStorage.setItem(
          "dm_refresh_token",
          tokenData?.refresh_token as string
        );

        if (userstatus === "active") {
          toast.success("Login successful!");
          localStorage.setItem("dm_isLoggedIn", "true");
          navigate("/inventory2");
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        toast.error("Wrong email or password");
        localStorage.clear();
        navigate("/");
        return;
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.toString() || "Authentication fail");
      setIsLoading(false);
      localStorage.clear();
      navigate("/");
    }
  };

  const revokeToken = async (
    token: string,
    tokenType: "access_token" | "refresh_token"
  ) => {
    //base64 authorization
    const basicAuthorozationSecret = concatAndEncodeBase64(
      CLIENT_URL as string,
      CLIENT_SECRET as string
    );
    try {
      const response = await fetch(
        `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/revoke`,
        {
          method: "POST",
          headers: {
            "XSM-Domain": USER_DOMAIN || "",
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${basicAuthorozationSecret}`,
          },
          body: new URLSearchParams({
            token,
            token_type_hint: tokenType,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to revoke token");
      }

      return true;
    } catch (error) {
      console.error("Error revoking token:", error);
      return false;
    }
  };

  // XSM-1536: SSO Login
  const handleLogout = async () => {
    try {
      // Revoke token with access token and refresh token
      await revokeToken(ACCESS_TOKEN, "access_token");
      await revokeToken(REFRESH_TOKEN, "refresh_token");

      // Construct logout URL with query parameters
      const url = new URL(`${XSM_BASE_URL}/xsm-api-sso/v2/proxy/oidc/logout`);
      const queryParams = new URLSearchParams({
        post_logout_redirect_uri: REACT_AUTH_ENDPOINT || "",
        "XSM-Domain": CLIENT_URL || "",
      });

      // Add id_token_hint if available
      queryParams.set("id_token_hint", ID_TOKEN);
      url.search = queryParams.toString();

      //delete localStorage and Cookie
      localStorage.removeItem("dm_access_token");
      localStorage.removeItem("dm_id_token");
      localStorage.removeItem("dm_domain");
      localStorage.removeItem("dm_refresh_token");
      localStorage.clear();
      // Redirect to logout endpoint
      // window.location.assign(url.toString());
      window.open(COMMON_CALLBACK_URL, "_self");
    } catch (error) {
      navigate("/");
    }
  };

  useEffect(() => {
    if (loginCode && hasProcessedLogin.current !== loginCode) {
      hasProcessedLogin.current = loginCode;
      handleSubmitLogin(loginCode);
    }
  }, [loginCode, handleSubmitLogin]);

  return {
    initialAuthorization,
    loginFormik,
    handleSubmitLogin,
    isLoading,
    handleLogout,
    state,
    setState,
  };
};
